import { useContext } from 'react';
import { useRouter } from 'next/router';
import ivclass from '@invideoio/web-shared/Modules/IVClass';

import AppLayout from '@layouts/AppLayout';

import { BlogHomepageCardPlatformType, PlatformDetailsType } from 'src/types';
import { StateContext } from '@store/store';
import { generateBreadcrumbsFromUrl } from '@utils/common.utils';
import { fetchAllPlatforms, fetchPlatformDetails } from '@lib/API/platformAPI';
import HeaderWithCTA from '@components/header-with-cta';
import PlatformHeaderSection from '@components/platform-header-section';
import Breadcrumb from '@components/breadcrumbs';
import PaginatedBlogList from '@components/paginated-blog-listing';
import SubHeading from '@components/sub-heading';
import ItemsTray from '@components/items-tray';
import PlatformCard from '@components/platforms/platform-card';
import BlogSEO from '@components/SEO/BlogSEO';
import SeeAllLink from '@components/see-all-link';
import BlogPostCard from '@components/blog-posts/blog-post-card';
import useBlogAppLoad from '@hooks/useBlogAppLoad';
import { GetServerSidePropsContext } from 'next';

interface platformType {
  platformDetails: PlatformDetailsType;
  otherPlatforms: BlogHomepageCardPlatformType[];
}

function PlatformBlogPage({ platformDetails, otherPlatforms }: platformType) {
  const globalState = useContext(StateContext);
  const { platform, browser } = globalState;

  const router = useRouter();

  const pagePathName = `/blog/platforms/${router.query.platform}/`;

  const { trendingPosts, name, slug } = platformDetails;

  useBlogAppLoad({ platform });

  return (
    <AppLayout platform={platform}>
      <BlogSEO seo={platformDetails?.SEO} url={pagePathName} />
      <HeaderWithCTA />
      <PlatformHeaderSection
        platform={platform}
        browser={browser}
        {...platformDetails}
      />
      {trendingPosts && trendingPosts.length > 0 && (
        <section className={ivclass('iv-bg-grey-7 iv-overflow-x-hidden')}>
          <div className={ivclass('iv-container iv-mx-auto')}>
            <div className={ivclass('iv-grid iv-grid-cols-12 iv-col-span-12')}>
              <div className={ivclass('iv-col-start-1 iv-col-span-12')}>
                <div
                  className={ivclass(
                    'iv-px-[0px] lg:iv-px-[73px] md:iv-px-25 sm:iv-px-20',
                    'iv-mt-[72px] lg:iv-mt-[70px] md:iv-mt-[45px] sm:iv-mt-[43px] ',
                    'iv-mb-[82px] lg:iv-mb-80 md:iv-mb-50 sm:iv-mb-[31px]',
                  )}
                >
                  <div
                    className={ivclass(
                      'iv-pl-[56px] lg:iv-pl-[39px] md:iv-pl-[32px] sm:iv-pl-[26px]',
                      'iv-mb-25 lg:iv-mb-[28px] md:iv-mb-[11px] sm:iv-mb-[12px]',
                    )}
                  >
                    <Breadcrumb
                      breadcrumbItems={generateBreadcrumbsFromUrl(
                        pagePathName.replace(/\/?$/, ''),
                      )}
                    />
                  </div>

                  <div className={ivclass('iv-container', 'iv-mx-auto')}>
                    <div className={ivclass('xl:iv-overflow-x-hidden')}>
                      <div
                        className={ivclass(
                          'iv-grid iv-grid-cols-12 iv-col-span-12',
                        )}
                      >
                        <div className={ivclass('iv-col-span-12')}>
                          <div
                            className={ivclass(
                              'iv-flex iv-justify-between iv-items-baseline',
                              'iv-mb-[67px] md:iv-mb-[32px] sm:iv-mb-15',
                              'iv-text-body-2xl iv-font-semibold ',
                            )}
                          >
                            <div
                              className={ivclass(
                                'iv-text-heading-2xl md:iv-text-heading-m sm:iv-text-[36px]',
                                'sm:iv-leading-[42px]',
                                'iv-font-extrabold',
                                'iv-pl-[56px] lg:iv-pl-[35px] md:iv-pl-[32px] sm:iv-pl-25',
                                'sm:iv-max-w-[280px]',
                              )}
                            >
                              {'Top trending posts 🔥'}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className={ivclass('sm:iv-mt-[7px]')}>
                        {trendingPosts && (
                          <ItemsTray
                            platform={platform}
                            sliderWrapperCls={ivclass(
                              'lg:iv-mr-20',
                              'md:iv-mr-[12px]',
                              'sm:iv-mr-20',
                            )}
                          >
                            {trendingPosts?.map(post => (
                              <div
                                key={post.id}
                                className={ivclass(
                                  'iv-relative',
                                  'iv-mr-40 md:iv-mr-25',
                                )}
                              >
                                <BlogPostCard
                                  {...post}
                                  browser={browser}
                                  devicePlatform={platform}
                                  backgroundColorClass={'iv-bg-grey-0'}
                                />
                              </div>
                            ))}
                          </ItemsTray>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
      <section
        className={ivclass(
          'iv-mb-100 lg:iv-mb-100 md:iv-mb-[80px] sm:iv-mb-50',
        )}
      >
        <div className={ivclass('iv-grid iv-grid-cols-12 iv-col-span-12')}>
          <div className={ivclass('iv-col-start-1 iv-col-span-12')}>
            <PaginatedBlogList
              listTypeKey={'platform'}
              listName={name.toLowerCase()}
              trendingPosts={trendingPosts}
              browserURL={pagePathName}
              slug={slug}
              browser={browser}
              devicePlatform={platform}
            />
          </div>
        </div>
      </section>
      <section
        className={ivclass(
          'iv-bg-black-4',
          'iv-pt-[92px] md:iv-pt-[72px] sm:iv-pt-[45px]',
          'iv-pb-100 md:iv-pb-80 sm:iv-pb-60',
        )}
      >
        <div
          className={ivclass('iv-container iv-mx-auto ', 'iv-overflow-hidden')}
        >
          <div className={ivclass('iv-grid iv-grid-cols-12')}>
            <div className={ivclass('iv-col-span-12')}>
              <div
                className={ivclass(
                  'lg:iv-px-[45px] md:iv-px-[21px] sm:iv-px-[19px]',
                  'sm:iv-mb-[21px]',
                )}
              >
                <SubHeading
                  title={'Other platforms'}
                  viewAllURL={'/blog/platforms/'}
                />
              </div>
              {otherPlatforms.length > 0 && (
                <div
                  className={ivclass('lg:iv-mx-[70px] md:iv-mx-25 sm:iv-mx-20')}
                >
                  <ItemsTray
                    platform={platform}
                    sliderWrapperCls={ivclass(
                      'lg:iv-mr-20',
                      'md:iv-mr-[12px]',
                      'sm:iv-mr-20',
                    )}
                  >
                    {otherPlatforms?.map(blogPlatform => (
                      <div
                        key={blogPlatform.id}
                        className={ivclass(
                          'iv-relative',
                          'iv-mr-40 md:iv-mr-25 ',
                        )}
                      >
                        <PlatformCard
                          browser={browser}
                          platform={platform}
                          {...blogPlatform}
                        />
                      </div>
                    ))}
                  </ItemsTray>
                  <div
                    className={ivclass(
                      'xl:iv-hidden lg:iv-hidden md:iv-hidden ',
                      'iv-flex iv-justify-center',
                      'sm:iv-mt-40',
                    )}
                  >
                    <SeeAllLink viewAllURL={'/blog/platforms/'} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </AppLayout>
  );
}

export default PlatformBlogPage;

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const { platform } = context.query;

  const lang = context.locale;

  try {
    if (typeof platform === 'string') {
      const platformDetailsPromise = fetchPlatformDetails(platform, lang);

      const otherPlatformsPromise = fetchAllPlatforms({
        listTypeToSkip: platform,
        language: lang,
      });

      const [platformDetails, otherPlatforms] = await Promise.all([
        platformDetailsPromise,
        otherPlatformsPromise,
      ]);

      return {
        props: { platformDetails, otherPlatforms },
      };
    }
  } catch (error: any) {
    console.log(error.message);
    return {
      props: { platformDetails: {}, otherPlatforms: [] },
    };
  }
}
